import React from 'react';
import { Header, Button } from './primitives.jsx';
import { Hero, SetupForm, PoolJoin, PoolSuccess } from './screens.jsx';
import { Walkout } from './Walkout.jsx';
import { Dashboard } from './Dashboard.jsx';
import { Predictor } from './Predictor.jsx';
import { Eliminator } from './Eliminator.jsx';
import { Bracket } from './Bracket.jsx';
import { ScoreUpdateModal, RerollModal, AdminPanel, RecoverModal, OnboardModal, TourModal, TOUR_STEPS, TOUR_VERSION } from './modals.jsx';
import { BrandedShell } from './BrandedShell.jsx';
import { PrivacyPolicy, TermsOfService, CookieNotice, Footer } from './legal.jsx';

// ============================================================================
// Main Application Routing & Controller
// ============================================================================

export const App = () => {
  const [screen, setScreen] = React.useState('loading');
  const [prevScreen, setPrevScreen] = React.useState('hero');
  const [pool, setPool] = React.useState(null);

  const [createdPoolData, setCreatedPoolData] = React.useState(null);
  const [myTeam, setMyTeam] = React.useState(null);
  const [adminSecret, setAdminSecret] = React.useState(null);

  const [editingMatch, setEditingMatch] = React.useState(null);
  const [rerollTeam, setRerollTeam] = React.useState(null);
  const [recoverOpen, setRecoverOpen] = React.useState(false);
  const [onboardOpen, setOnboardOpen] = React.useState(false);
  const [tourStep, setTourStep] = React.useState(null); // null = tour closed

  const parseRoute = async () => {
    const path = window.location.pathname;

    // Legal pages — static, no data fetching needed. Handled BEFORE /pool/
    // so they always render regardless of any in-memory pool state.
    if (path === '/privacy') { setScreen('privacy'); return; }
    if (path === '/terms')   { setScreen('terms');   return; }
    if (path === '/cookies') { setScreen('cookies'); return; }

    if (path.startsWith('/pool/')) {
      const parts = path.split('/');
      const poolId = parts[2];

      if (poolId) {
        setScreen('loading');
        try {
          const res = await fetch(`/api/pool/${poolId}`);
          if (res.ok) {
            const data = await res.json();
            setPool(data);

            const storedSecret = localStorage.getItem(`sweep-wc26-secret-${poolId}`);
            if (storedSecret) setAdminSecret(storedSecret);

            const drawnTeamId = localStorage.getItem(`sweep-wc26-drawn-${poolId}`);
            if (drawnTeamId) {
              const matchedTeam = data.teams.find(t => t.id === drawnTeamId);
              if (matchedTeam) setMyTeam(matchedTeam);
            }

            if (data.identity_mode === 'handle') {
              setScreen('branded');
            } else if (data.stage === 'bracket') {
              setScreen('bracket');
            } else {
              setScreen('dashboard');
            }
          } else {
            setScreen('hero');
            alert('Pool not found.');
          }
        } catch (err) {
          setScreen('hero');
          alert('Error loading pool details.');
        }
      } else {
        setScreen('hero');
      }
    } else if (/^\/[a-z0-9-]{2,24}\/?$/i.test(path)) {
      const candidate = path.replace(/^\//, '').replace(/\/$/, '');
      setScreen('loading');
      try {
        const res = await fetch(`/api/pool/by-slug/${encodeURIComponent(candidate)}`);
        if (res.ok) {
          const data = await res.json();
          setPool(data);
          const storedSecret = localStorage.getItem(`sweep-wc26-secret-${data.id}`);
          if (storedSecret) setAdminSecret(storedSecret);
          setScreen(data.identity_mode === 'handle' ? 'branded' : 'dashboard');
        } else {
          setScreen('notfound');
        }
      } catch {
        setScreen('notfound');
      }
    } else {
      setScreen('hero');
    }
  };

  React.useEffect(() => {
    parseRoute();

    const handlePopState = () => {
      parseRoute();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Auto-open the onboarding popup for a brand-new player landing on a pool:
  // no team yet, the draft is still open, and they haven't dismissed it before.
  React.useEffect(() => {
    if (screen !== 'dashboard' || !pool || myTeam) return;
    const draftOpen = pool.stage === 'group' && (pool.current_round ?? 1) <= (pool.rounds ?? 1);
    const dismissed = localStorage.getItem(`sweep-wc26-onboard-dismissed-${pool.id}`);
    if (draftOpen && !dismissed) setOnboardOpen(true);
  }, [screen, pool, myTeam]);

  const dismissOnboard = () => {
    if (pool) localStorage.setItem(`sweep-wc26-onboard-dismissed-${pool.id}`, '1');
    setOnboardOpen(false);
  };

  // Player launch tour: open once per version for a pool MEMBER (someone who has
  // drawn a team — new or returning) landing on the dashboard. Distinct from the
  // admin-only "What's New" briefing; re-launchable from the dashboard.
  React.useEffect(() => {
    if (screen !== 'dashboard' || !pool || !myTeam || tourStep !== null) return;
    if (!localStorage.getItem(`sweep-wc26-tour-${TOUR_VERSION}`)) setTourStep(0);
  }, [screen, pool, myTeam]);

  // Each tour step shows its own screen behind the bottom-anchored card.
  React.useEffect(() => {
    if (tourStep === null) return;
    const target = TOUR_STEPS[tourStep]?.screen;
    if (target && target !== screen) transitionTo(target);
  }, [tourStep]);

  const endTour = () => {
    try { localStorage.setItem(`sweep-wc26-tour-${TOUR_VERSION}`, '1'); } catch { /* private mode */ }
    setTourStep(null);
    transitionTo('dashboard');
  };
  const nextTourStep = () => {
    if (tourStep === null) return;
    if (tourStep < TOUR_STEPS.length - 1) setTourStep(tourStep + 1);
    else endTour();
  };
  const backTourStep = () => setTourStep((s) => (s && s > 0 ? s - 1 : 0));

  // Scroll-to-top on every screen change. Single-page-app navigation doesn't
  // reset scroll position by default — without this, clicking "How it works"
  // from a deep dashboard scroll leaves you mid-page on the Rules screen.
  const scrollToTop = () => {
    // 'instant' so users don't see a smooth scroll-up before the new screen
    // appears — the perception should be "fresh page", not "still scrolling".
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const transitionTo = (screenName) => {
    setPrevScreen(screen);
    setScreen(screenName);
    scrollToTop();
  };

  const navigateTo = (path, screenName) => {
    window.history.pushState({}, '', path);
    if (screenName) {
      setPrevScreen(screen);
      setScreen(screenName);
    } else {
      parseRoute();
    }
    scrollToTop();
  };

  const handlePoolCreated = (newPool) => {
    localStorage.setItem(`sweep-wc26-secret-${newPool.id}`, newPool.secret);
    setAdminSecret(newPool.secret);
    setMyTeam(null);
    setCreatedPoolData(newPool);
    navigateTo(`/pool/${newPool.id}`, 'success');
  };

  // Returns { ok: true } on success, or { ok: false, error } so callers (the
  // onboarding popup, the join screen) can show the failure inline.
  const handlePickTeam = async (participantName, participantEmail) => {
    if (!pool) return { ok: false, error: 'No pool loaded.' };
    try {
      const res = await fetch(`/api/pool/${pool.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: participantName, ...(participantEmail && { email: participantEmail }) })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem(`sweep-wc26-drawn-${pool.id}`, data.team.id);
        // Remember WHO this device is so later rounds don't require re-typing
        // the name (prevents duplicate/misspelled players).
        try { localStorage.setItem(`sweep-wc26-player-${pool.id}`, participantName); } catch { /* private mode */ }

        const refreshRes = await fetch(`/api/pool/${pool.id}`);
        const freshPool = await refreshRes.json();
        setPool(freshPool);

        const fullTeam = freshPool.teams.find(t => t.id === data.team.id);
        setMyTeam(fullTeam);

        setOnboardOpen(false);
        setScreen('walkout');
        return { ok: true };
      }

      // The /assign endpoint returns structured error codes (D2) so we can
      // show specific copy instead of dumping the raw server message.
      const friendly = {
        already_drew_this_round: data.error,
        pool_full: data.error,
        draft_complete: data.error,
        no_teams_left: 'All 48 teams have been drawn — the tournament pool is empty.',
      }[data.code] || data.error || 'Failed to draw team.';

      // Even on failure, refresh pool so the dashboard reflects the latest
      // state (e.g. a player who tried to draw twice still sees others' picks).
      try {
        const refreshRes = await fetch(`/api/pool/${pool.id}`);
        if (refreshRes.ok) setPool(await refreshRes.json());
      } catch { /* best effort */ }

      return { ok: false, error: friendly };
    } catch (err) {
      return { ok: false, error: 'Network error drawing team.' };
    }
  };

  const handleUnlockAdmin = (secret) => {
    if (secret) {
      localStorage.setItem(`sweep-wc26-secret-${pool.id}`, secret);
      setAdminSecret(secret);
    } else {
      localStorage.removeItem(`sweep-wc26-secret-${pool.id}`);
      setAdminSecret(null);
    }
  };

  if (screen === 'loading') {
    return (
      <div style={{
        minHeight: 'var(--h-full)', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', background: 'var(--paper)',
        fontFamily: 'var(--font-display)', color: 'var(--ink)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/assets/trophy.svg" alt="" style={{ width: 48, animation: 'pulse-ring 1.5s infinite' }} />
          <h2 style={{ fontSize: 32, letterSpacing: '0.04em', textTransform: 'uppercase', margin: 0 }}>LOADING...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        poolId={pool?.id}
        screen={screen}
        onNav={(k) => {
          if (k === 'hero') navigateTo('/');
          else if (k === 'exit') {
            if (confirm('Exit the current pool and go back to home?')) {
              setPool(null);
              setMyTeam(null);
              setAdminSecret(null);
              navigateTo('/', 'hero');
            }
          } else {
            transitionTo(k);
          }
        }}
      />

      <main>
        {screen === 'hero' && (
          <Hero
            onCreate={() => transitionTo('setup')}
            onRules={() => transitionTo('rules')}
            onAbout={() => { window.location.href = '/about'; }}
            onOpenPool={(poolId) => navigateTo(`/pool/${poolId}`)}
            onOpenRecover={() => setRecoverOpen(true)}
          />
        )}

        {screen === 'setup' && (
          <SetupForm
            onCreated={handlePoolCreated}
            onBack={() => transitionTo('hero')}
          />
        )}

        {screen === 'success' && createdPoolData && (
          <PoolSuccess
            poolId={createdPoolData.id}
            poolName={createdPoolData.name}
            slug={createdPoolData.slug}
            adminSecret={createdPoolData.secret}
            adminEmail={createdPoolData.email}
            onProceed={() => navigateTo(createdPoolData.slug ? `/${createdPoolData.slug}` : `/pool/${createdPoolData.id}`)}
            onBackHome={() => navigateTo('/', 'hero')}
          />
        )}

        {screen === 'join' && pool && (
          <PoolJoin
            pool={pool}
            assignedCount={pool.teams.filter(t => t.assigned_to).length}
            onPick={handlePickTeam}
            onBack={() => transitionTo('dashboard')}
          />
        )}

        {screen === 'walkout' && myTeam && (
          <Walkout
            team={myTeam}
            onDone={() => transitionTo(pool?.stage === 'bracket' ? 'bracket' : 'dashboard')}
          />
        )}

        {screen === 'branded' && pool && (
          <BrandedShell pool={pool} />
        )}

        {screen === 'dashboard' && pool && (
          <Dashboard
            pool={pool}
            assignments={{}}
            myTeam={myTeam}
            onPickTeam={() => setOnboardOpen(true)}
            onGoBracket={() => transitionTo('bracket')}
            onRefresh={() => parseRoute()}
            isAdmin={!!adminSecret}
            adminSecret={adminSecret}
            onOpenReroll={(t) => setRerollTeam(t)}
            onStartTour={() => setTourStep(0)}
          />
        )}

        {screen === 'predictor' && pool && (
          <section style={{ padding: '128px 32px 80px', maxWidth: 1120, margin: '0 auto' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
              flexWrap: 'wrap', gap: 24, marginBottom: 28,
              paddingBottom: 20, borderBottom: '2px solid var(--ink)',
            }}>
              <div>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: 'var(--blue)',
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                }}>
                  GAME MODE
                  <span style={{
                    background: 'var(--gold)', color: 'var(--ink)',
                    fontSize: 10, letterSpacing: '0.12em',
                    padding: '2px 7px', borderRadius: 999, lineHeight: 1,
                  }}>NEW</span>
                </span>
                <h1 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(48px, 6vw, 88px)',
                  letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.9,
                  margin: '10px 0 8px',
                }}>PREDICTOR</h1>
                <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 15, maxWidth: 640 }}>
                  Super 6 scoring · exact 5pt · result+GD 3pt · result 1pt. Picks lock at kick-off. Played alongside the sweep — separate scoreboard.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button variant="secondary" onClick={() => transitionTo('dashboard')} style={{ whiteSpace: 'nowrap' }}>
                  ← DASHBOARD
                </Button>
                <Button variant="secondary" onClick={() => transitionTo('bracket')} style={{ whiteSpace: 'nowrap' }}>
                  VIEW BRACKET →
                </Button>
              </div>
            </div>
            <Predictor pool={pool} myTeam={myTeam} />
          </section>
        )}

        {screen === 'eliminator' && pool && (
          <section style={{ padding: '128px 32px 80px', maxWidth: 1120, margin: '0 auto' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
              flexWrap: 'wrap', gap: 24, marginBottom: 28,
              paddingBottom: 20, borderBottom: '2px solid var(--ink)',
            }}>
              <div>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: 'var(--blue)',
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                }}>
                  GAME MODE
                  <span style={{
                    background: 'var(--gold)', color: 'var(--ink)',
                    fontSize: 10, letterSpacing: '0.12em',
                    padding: '2px 7px', borderRadius: 999, lineHeight: 1,
                  }}>NEW</span>
                </span>
                <h1 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(48px, 6vw, 88px)',
                  letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.9,
                  margin: '10px 0 8px',
                }}>ELIMINATOR</h1>
                <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 15, maxWidth: 640 }}>
                  Last man standing. Pick a team to win each round — no team twice. Two lives in the group, sudden death in the knockouts. Last one standing takes it.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button variant="secondary" onClick={() => transitionTo('dashboard')} style={{ whiteSpace: 'nowrap' }}>
                  ← DASHBOARD
                </Button>
                <Button variant="secondary" onClick={() => transitionTo('predictor')} style={{ whiteSpace: 'nowrap' }}>
                  PREDICTOR →
                </Button>
              </div>
            </div>
            <Eliminator pool={pool} myTeam={myTeam} />
          </section>
        )}

        {screen === 'bracket' && pool && (
          <Bracket
            pool={pool}
            onBack={() => transitionTo('dashboard')}
            onEditMatch={(m) => setEditingMatch(m)}
            isAdmin={!!adminSecret}
          />
        )}

        {screen === 'rules' && (
          <section style={{ padding: '128px 32px 80px', maxWidth: 880, margin: '0 auto' }}>
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--blue)',
            }}>HOW IT WORKS</span>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(56px, 7vw, 96px)',
              letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.9,
              margin: '12px 0 48px',
            }}>RULES</h1>
            <ol style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[
                ['Create the pool', 'Admin sets the pool name, email, number of players, and how many rounds the draft runs for.'],
                ['Share the QR', 'Colleagues scan or visit the URL. No login — pool ID is the only auth.'],
                ['Pick your team', 'One random team per person per round. Locked once revealed. Re-roll only by admin, 24h cooldown.'],
                ['Group stage', 'Track results in the dashboard. Top 2 per group + 8 best 3rd-placed advance.'],
                ['Knockout', 'R32 → R16 → QF → SF → Final. Admin advances winners after each matchday.'],
                ['Predict matches', 'Type a score for each fixture before kickoff. Super 6 scoring: exact 5pt · result+GD 3pt · result 1pt · wrong 0pt. Predictions lock the moment a match kicks off.'],
                ['Win the tournament', 'Pick who lifts the trophy. 50pt if you call it right — half a sweepstake on one bet. Locks when the bracket starts.'],
              ].map(([t, d], i, arr) => (
                <li key={i} style={{
                  display: 'grid', gridTemplateColumns: '80px 1fr',
                  gap: 24, padding: '24px 0',
                  borderBottom: i < arr.length - 1 ? '1px solid var(--hairline)' : 'none',
                }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 48, color: 'var(--blue)', lineHeight: 1 }}>0{i+1}</span>
                  <div>
                    <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, textTransform: 'uppercase', letterSpacing: '-0.005em', lineHeight: 1 }}>{t.toUpperCase()}</h3>
                    <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 16 }}>{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {screen === 'privacy' && <PrivacyPolicy onBack={() => navigateTo('/', 'hero')} />}
        {screen === 'terms'   && <TermsOfService onBack={() => navigateTo('/', 'hero')} />}
        {screen === 'cookies' && <CookieNotice  onBack={() => navigateTo('/', 'hero')} />}

        {screen === 'notfound' && (
          <section style={{ minHeight: 'var(--h-full)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 16, padding: '128px 24px', textAlign: 'center' }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(40px,6vw,72px)',
              textTransform: 'uppercase', margin: 0 }}>NO GAME HERE</h1>
            <p style={{ color: 'var(--ink-soft)', fontSize: 16, maxWidth: 420 }}>
              There's no game at this address. Check the link, or head back home.
            </p>
            <Button variant="primary" onClick={() => navigateTo('/', 'hero')}>BACK HOME</Button>
          </section>
        )}
      </main>

      {/* Footer on every screen except the cinematic walkout — that screen
          is full-bleed by design and a footer would break the spell. */}
      {screen !== 'walkout' && screen !== 'loading' && (
        <Footer onNav={(path) => navigateTo(path)} />
      )}

      {pool && screen !== 'walkout' && (
        <AdminPanel
          pool={pool}
          adminSecret={adminSecret}
          onUnlock={handleUnlockAdmin}
          onUpdatePool={(updatedPool) => setPool(updatedPool)}
        />
      )}

      {editingMatch && pool && (
        <ScoreUpdateModal
          match={editingMatch}
          pool={pool}
          adminSecret={adminSecret}
          onSave={(updatedPool) => setPool(updatedPool)}
          onClose={() => setEditingMatch(null)}
        />
      )}

      {rerollTeam && pool && (
        <RerollModal
          team={rerollTeam}
          pool={pool}
          adminSecret={adminSecret}
          onSave={(updatedPool) => setPool(updatedPool)}
          onClose={() => setRerollTeam(null)}
        />
      )}

      {recoverOpen && (
        <RecoverModal onClose={() => setRecoverOpen(false)} />
      )}

      {onboardOpen && pool && !myTeam && screen === 'dashboard' && (
        <OnboardModal
          pool={pool}
          assignedCount={pool.teams.filter(t => t.assigned_to).length}
          onPick={handlePickTeam}
          onClose={dismissOnboard}
        />
      )}

      {tourStep !== null && pool && screen !== 'walkout' && (
        <TourModal
          step={tourStep}
          onBack={backTourStep}
          onNext={nextTourStep}
          onSkip={endTour}
        />
      )}
    </>
  );
};
